import React, { useEffect, useState } from "react";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Calendar, CheckCircle2, IndianRupee, ArrowRight, ShoppingBag, Sparkles } from "lucide-react";
import { toast } from "sonner";
import FollowUpsPanel from "@/components/bookings/FollowUpsPanel";

const FLOW = [
  { id: "booked", label: "Booked" },
  { id: "scheduled", label: "Scheduled" },
  { id: "sample_collected", label: "Sample collected" },
  { id: "in_transit", label: "In transit" },
  { id: "at_lab", label: "At lab" },
  { id: "report_ready", label: "Report ready" },
  { id: "delivered", label: "Delivered" },
  { id: "cancelled", label: "Cancelled" },
];

export default function LabBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("tab");
    return p === "followups" ? "followups" : "marketplace";
  });
  const load = () => {
    setLoading(true);
    return api.get("/bookings").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const revenue = bookings.reduce((s, b) => s + Number(b.total_amount || 0), 0);
  const pending = bookings.filter((b) => !["delivered", "cancelled"].includes(b.booking_status)).length;

  return (
    <DashboardLayout role="lab_admin">
      <h1 className="font-heading font-bold text-3xl text-ink">Bookings</h1>
      <p className="text-ink/60">Marketplace bookings and Smart-Report follow-ups in one place.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList data-testid="lab-bookings-tabs">
          <TabsTrigger value="marketplace" data-testid="tab-marketplace"><ShoppingBag className="w-4 h-4 mr-2" /> Marketplace</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups"><Sparkles className="w-4 h-4 mr-2" /> Follow-ups</TabsTrigger>
        </TabsList>

        <TabsContent value="marketplace" className="mt-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <Mini icon={Calendar} color="text-efgh-orange" bg="bg-efgh-orange/10" value={pending} label="Active bookings" />
            <Mini icon={CheckCircle2} color="text-efgh-green" bg="bg-efgh-green/10" value={bookings.length} label="Total this month" />
            <Mini icon={IndianRupee} color="text-efgh-yellow" bg="bg-efgh-yellow/10" value={`₹${(revenue/1000).toFixed(1)}K`} label="Revenue" />
          </div>

          <Card className="mt-6 rounded-2xl overflow-hidden">
            <div className="hidden lg:grid grid-cols-[1.5fr_1.4fr_1fr_1fr_1fr_0.6fr] gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
              <div>Patient</div><div>Tests / Package</div><div>Slot</div><div>Amount</div><div>Status</div><div />
            </div>
            {loading && (
              <div className="p-6 space-y-3">
                {[0,1,2,3].map((i) => (<div key={i} className="h-14 rounded-xl bg-muted/40 animate-pulse" />))}
              </div>
            )}
            {!loading && bookings.length === 0 && <div className="p-10 text-center text-ink/50">No marketplace bookings yet.</div>}
            {bookings.map((b) => {
              const statusLabel = FLOW.find(f => f.id === b.booking_status)?.label || b.booking_status;
              const statusClass = b.booking_status === "delivered" ? "pill-normal" : b.booking_status === "cancelled" ? "pill-high" : "pill-borderline";
              const itemCount = (b.tests || []).length + (b.package_ids || []).length;
              return (
                <div key={b.id} data-testid={`lb-${b.id}`} className="border-b border-border">
                  {/* Desktop row */}
                  <div className="hidden lg:grid grid-cols-[1.5fr_1.4fr_1fr_1fr_1fr_0.6fr] gap-3 px-5 py-3 items-center">
                    <div>
                      <div className="font-medium text-ink">{b.patient_name}</div>
                      <div className="text-xs text-ink/50">{b.patient_phone}</div>
                    </div>
                    <div className="text-sm text-ink/70">{itemCount} item(s)</div>
                    <div className="text-sm text-ink/70">{b.slot_date} · {b.slot_time}</div>
                    <div className="font-heading font-semibold text-ink">₹{b.total_amount}</div>
                    <div><span className={`pill ${statusClass}`}>{statusLabel}</span></div>
                    <div className="text-right"><Button size="sm" variant="outline" onClick={() => setSelected(b)} data-testid={`advance-${b.id}`}>Advance</Button></div>
                  </div>
                  {/* Mobile card */}
                  <div className="lg:hidden px-4 py-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-medium text-ink truncate">{b.patient_name}</div>
                      <span className={`pill ${statusClass} shrink-0 text-[10px]`}>{statusLabel}</span>
                    </div>
                    <div className="text-xs text-ink/50 mt-0.5">{b.patient_phone}</div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink/70">
                      <span>{itemCount} item(s)</span>
                      <span>{b.slot_date} · {b.slot_time}</span>
                      <span className="font-heading font-semibold text-ink">₹{b.total_amount}</span>
                    </div>
                    <div className="mt-2"><Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSelected(b)} data-testid={`advance-m-${b.id}`}>Advance status</Button></div>
                  </div>
                </div>
              );
            })}
          </Card>
        </TabsContent>

        <TabsContent value="followups" className="mt-5">
          <FollowUpsPanel role="lab_admin" />
        </TabsContent>
      </Tabs>

      <AdvanceDialog booking={selected} onClose={() => setSelected(null)} onDone={() => { setSelected(null); load(); }} />
    </DashboardLayout>
  );
}

function AdvanceDialog({ booking, onClose, onDone }) {
  const [next, setNext] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (!booking) return;
    const curIdx = FLOW.findIndex((f) => f.id === booking.booking_status);
    setNext(FLOW[Math.min(curIdx + 1, FLOW.length - 2)]?.id || "delivered");
    setNote("");
  }, [booking]);

  if (!booking) return null;
  const advance = async () => {
    try {
      await api.patch(`/bookings/${booking.id}/status`, { status: next, note });
      toast.success(`Status updated to "${FLOW.find(f => f.id === next)?.label}"`);
      onDone();
    } catch (err) { toast.error(apiError(err, "Update failed")); }
  };

  return (
    <Dialog open={!!booking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="advance-dialog">
        <DialogTitle className="font-heading">Advance booking status</DialogTitle>
        <div className="text-sm text-ink/60">Ref: {booking.id.slice(0, 8).toUpperCase()} · {booking.patient_name}</div>

        <div className="mt-4 space-y-3">
          <div>
            <label className="text-xs text-ink/60 uppercase tracking-wider">Next status</label>
            <Select value={next} onValueChange={setNext}>
              <SelectTrigger data-testid="advance-status"><SelectValue /></SelectTrigger>
              <SelectContent>{FLOW.map((f) => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-ink/60 uppercase tracking-wider">Note (optional)</label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Phlebotomist Rajesh assigned" />
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={advance} data-testid="confirm-advance" className="bg-ink hover:bg-ink/90 text-white"><ArrowRight className="w-4 h-4 mr-2" />Update status</Button>
        </div>

        {booking.status_history && booking.status_history.length > 0 && (
          <div className="mt-5 pt-5 border-t border-border">
            <div className="text-xs uppercase tracking-widest text-ink/50 mb-2">History</div>
            <ul className="space-y-2 text-xs text-ink/70">
              {booking.status_history.slice().reverse().map((h, i) => (
                <li key={i}><b className="capitalize">{h.status.replace(/_/g, " ")}</b> — {new Date(h.at).toLocaleString()}{h.note ? ` · ${h.note}` : ""}</li>
              ))}
            </ul>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Mini({ icon: Icon, color, bg, value, label }) {
  return (
    <Card className="p-5 rounded-2xl">
      <div className={`w-10 h-10 rounded-xl ${bg} ${color} grid place-items-center`}><Icon className="w-5 h-5" /></div>
      <div className="mt-3 font-heading font-bold text-3xl text-ink">{value}</div>
      <div className="text-xs text-ink/50 uppercase tracking-wider mt-1">{label}</div>
    </Card>
  );
}
